import { Pencil, Trash2 } from 'lucide-react-native';
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { IconButton } from '../../../components/primitives/IconButton';
import { Input } from '../../../components/primitives/Input';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { DeleteProductDialog } from '../../onboarding/components/DeleteProductDialog';
import { ProductAliasesInput } from '../../onboarding/components/ProductAliasesInput';
import { ProductKindAliasesSummary } from '../../onboarding/components/ProductKindAliasesSummary';
import { ProductKindSelector } from '../../onboarding/components/ProductKindSelector';
import { ProductNameGuidance } from '../../onboarding/components/ProductNameGuidance';
import {
  useCreateOrganizationProduct,
  useDeleteOrganizationProduct,
  useOrganizationProducts,
  useUpdateOrganizationProduct,
} from '../../onboarding/hooks';
import { useProductMutationFeedback } from '../../onboarding/hooks/useProductMutationFeedback';
import type { OrganizationProduct } from '../../onboarding/types';
import { productErrorMessageParams } from '../../onboarding/utils/productErrors';
import {
  MAX_PRODUCT_DESCRIPTION_LENGTH,
  MAX_PRODUCT_NAME_LENGTH,
  MAX_PRODUCT_PRICE_RANGE_LENGTH,
} from '../../onboarding/utils/productLimits';
import {
  buildCreateProductExtras,
  buildProductUpdateChanges,
  EMPTY_PRODUCT_FIELDS,
  type ProductFormFields,
  type ProductTextFields,
  productToFormFields,
} from '../../onboarding/utils/productPayload';

interface RowEdit extends ProductFormFields {
  id: string;
}

interface ProductCatalogEditorProps {
  organizationId: string;
  canEdit: boolean;
}

/**
 * Post-onboarding editor for the organization's product catalog.
 *
 * Every action persists on its own round trip instead of collecting a draft
 * like the onboarding wizard does: there is no "finish" step here to flush a
 * batch, and the agents read this catalog as soon as it changes.
 */
export function ProductCatalogEditor({
  organizationId,
  canEdit,
}: Readonly<ProductCatalogEditorProps>) {
  const { t } = useTranslation();
  const { data: products } = useOrganizationProducts(organizationId);
  const createMutation = useCreateOrganizationProduct();
  const updateMutation = useUpdateOrganizationProduct();
  const deleteMutation = useDeleteOrganizationProduct();

  const [draft, setDraft] = useState<ProductFormFields>(EMPTY_PRODUCT_FIELDS);
  const [rowEdit, setRowEdit] = useState<RowEdit | null>(null);
  const [pendingDelete, setPendingDelete] = useState<OrganizationProduct | null>(null);
  const feedback = useProductMutationFeedback();

  const catalog = products ?? [];
  const trimmedDraftName = draft.name.trim();
  const canCreate = trimmedDraftName.length > 0 && !createMutation.isPending;

  const setDraftField = (field: keyof ProductTextFields) => (text: string) => {
    setDraft((prev) => ({ ...prev, [field]: text }));
  };

  const setRowField = (field: keyof ProductTextFields) => (text: string) => {
    setRowEdit((prev) => (prev ? { ...prev, [field]: text } : prev));
  };

  const handleCreate = () => {
    if (!canCreate) return;

    createMutation.mutate(
      { organizationId, name: trimmedDraftName, ...buildCreateProductExtras(draft) },
      {
        ...feedback.callbacks,
        onSuccess: () => {
          feedback.callbacks.onSuccess();
          setDraft(EMPTY_PRODUCT_FIELDS);
        },
      },
    );
  };

  const handleUpdate = () => {
    if (!rowEdit || updateMutation.isPending) return;

    const name = rowEdit.name.trim();
    if (!name) return;

    const loaded = catalog.find((product) => product.id === rowEdit.id);

    updateMutation.mutate(
      {
        organizationId,
        productId: rowEdit.id,
        name,
        description: rowEdit.description.trim(),
        priceRange: rowEdit.priceRange.trim(),
        ...(loaded ? buildProductUpdateChanges(loaded, rowEdit) : {}),
      },
      {
        ...feedback.callbacks,
        onSuccess: () => {
          feedback.callbacks.onSuccess();
          setRowEdit(null);
        },
      },
    );
  };

  const applyDraftSuggestion = (suggestion: string) => {
    setDraft((prev) => ({ ...prev, name: suggestion, description: '' }));
  };

  const applyRowSuggestion = (suggestion: string) => {
    setRowEdit((prev) => (prev ? { ...prev, name: suggestion, description: '' } : prev));
  };

  const editWithSuggestedName = (product: OrganizationProduct, suggestion: string) => {
    setRowEdit({
      id: product.id,
      ...productToFormFields(product),
      name: suggestion,
      description: '',
    });
  };

  const handleDelete = (productId: string) => {
    setPendingDelete(null);
    if (rowEdit?.id === productId) setRowEdit(null);
    deleteMutation.mutate({ organizationId, productId }, feedback.callbacks);
  };

  const renderRowEditor = (product: OrganizationProduct, edit: RowEdit) => (
    <VStack spacing="sm">
      <Input
        testID={`business_context_products_edit_name_${product.id}`}
        maxLength={MAX_PRODUCT_NAME_LENGTH}
        label={t('onboardingWizard.products.nameLabel')}
        accessibilityLabel={`${t('onboardingWizard.products.nameLabel')}: ${product.name}`}
        value={edit.name}
        onChangeText={setRowField('name')}
        placeholder={t('onboardingWizard.products.namePlaceholder')}
      />
      <ProductNameGuidance
        testIDPrefix={`business_context_products_edit_guidance_${product.id}`}
        name={edit.name}
        description={edit.description}
        priceRange={edit.priceRange}
        aliases={edit.aliases}
        onUseSuggestion={applyRowSuggestion}
      />
      <Input
        testID={`business_context_products_edit_description_${product.id}`}
        maxLength={MAX_PRODUCT_DESCRIPTION_LENGTH}
        label={t('onboardingWizard.products.descriptionLabel')}
        accessibilityLabel={`${t('onboardingWizard.products.descriptionLabel')}: ${product.name}`}
        value={edit.description}
        onChangeText={setRowField('description')}
        placeholder={t('onboardingWizard.products.descriptionPlaceholder')}
        multiline
      />
      <Input
        testID={`business_context_products_edit_priceRange_${product.id}`}
        maxLength={MAX_PRODUCT_PRICE_RANGE_LENGTH}
        label={t('onboardingWizard.products.priceRangeLabel')}
        accessibilityLabel={`${t('onboardingWizard.products.priceRangeLabel')}: ${product.name}`}
        value={edit.priceRange}
        onChangeText={setRowField('priceRange')}
        placeholder={t('onboardingWizard.products.priceRangePlaceholder')}
      />
      <ProductKindSelector
        testIDPrefix={`business_context_products_edit_kind_${product.id}`}
        value={edit.kind}
        onChange={(kind) => setRowEdit((prev) => (prev ? { ...prev, kind } : prev))}
      />
      <ProductAliasesInput
        testIDPrefix={`business_context_products_edit_aliases_${product.id}`}
        aliases={edit.aliases}
        productName={edit.name}
        onChange={(aliases) => setRowEdit((prev) => (prev ? { ...prev, aliases } : prev))}
      />
      <HStack spacing="sm">
        <Button
          testID={`business_context_products_save_${product.id}`}
          variant="primary"
          size="sm"
          onPress={handleUpdate}
          loading={updateMutation.isPending}
          disabled={edit.name.trim().length === 0 || updateMutation.isPending}
          accessibilityLabel={t('settings.businessContext.products.save')}
        >
          {t('settings.businessContext.products.save')}
        </Button>
        <Button
          testID={`business_context_products_cancel_${product.id}`}
          variant="outline"
          size="sm"
          onPress={() => setRowEdit(null)}
          accessibilityLabel={t('settings.businessContext.products.cancel')}
        >
          {t('settings.businessContext.products.cancel')}
        </Button>
      </HStack>
    </VStack>
  );

  const renderRowSummary = (product: OrganizationProduct) => (
    <VStack spacing="sm">
      <HStack spacing="sm" align="center" justify="between">
        <VStack spacing="xxs" fill>
          <Text variant="body" color={colors.text.primary}>
            {product.name}
          </Text>
          {product.description ? (
            <Text variant="caption" color={colors.text.secondary}>
              {product.description}
            </Text>
          ) : null}
          {product.priceRange ? (
            <Text variant="caption" color={colors.text.secondary}>
              {product.priceRange}
            </Text>
          ) : null}
          <ProductKindAliasesSummary
            testID={`business_context_products_meta_${product.id}`}
            product={product}
          />
        </VStack>
        {canEdit ? (
          <HStack spacing="xs" align="center">
            <IconButton
              testID={`business_context_products_edit_${product.id}`}
              icon={<Pencil size={16} color={colors.text.secondary} strokeWidth={2} />}
              variant="ghost"
              size="sm"
              onPress={() => setRowEdit({ id: product.id, ...productToFormFields(product) })}
              accessibilityLabel={`${t('settings.businessContext.products.edit')}: ${product.name}`}
            />
            <IconButton
              testID={`business_context_products_remove_${product.id}`}
              icon={<Trash2 size={16} color={colors.text.secondary} strokeWidth={2} />}
              variant="ghost"
              size="sm"
              onPress={() => setPendingDelete(product)}
              accessibilityLabel={`${t('settings.businessContext.products.remove')}: ${product.name}`}
            />
          </HStack>
        ) : null}
      </HStack>
      {canEdit ? (
        <ProductNameGuidance
          testIDPrefix={`business_context_products_notice_${product.id}`}
          name={product.name}
          description={product.description}
          showPreview={false}
          variant="saved"
          onUseSuggestion={(suggestion) => editWithSuggestedName(product, suggestion)}
        />
      ) : null}
    </VStack>
  );

  return (
    <VStack spacing="md" testID="business_context_products">
      <VStack spacing="xxs">
        <Text variant="body" color={colors.text.primary} style={styles.heading}>
          {t('settings.businessContext.products.title')}
        </Text>
        <Text variant="caption" color={colors.text.secondary}>
          {t('settings.businessContext.products.subtitle')}
        </Text>
      </VStack>

      {catalog.length === 0 ? (
        <Text
          testID="business_context_products_empty"
          variant="bodySmall"
          color={colors.text.secondary}
        >
          {t('settings.businessContext.products.empty')}
        </Text>
      ) : (
        <VStack spacing="sm">
          {catalog.map((product) => (
            <Box
              key={product.id}
              testID={`business_context_products_item_${product.id}`}
              style={styles.row}
            >
              {rowEdit?.id === product.id
                ? renderRowEditor(product, rowEdit)
                : renderRowSummary(product)}
            </Box>
          ))}
        </VStack>
      )}

      {canEdit ? (
        <VStack spacing="sm">
          <Input
            testID="business_context_products_name"
            maxLength={MAX_PRODUCT_NAME_LENGTH}
            label={t('onboardingWizard.products.nameLabel')}
            value={draft.name}
            onChangeText={setDraftField('name')}
            placeholder={t('onboardingWizard.products.namePlaceholder')}
          />
          <ProductNameGuidance
            testIDPrefix="business_context_products_guidance"
            name={draft.name}
            description={draft.description}
            priceRange={draft.priceRange}
            aliases={draft.aliases}
            onUseSuggestion={applyDraftSuggestion}
          />
          <Input
            testID="business_context_products_description"
            maxLength={MAX_PRODUCT_DESCRIPTION_LENGTH}
            label={t('onboardingWizard.products.descriptionLabel')}
            value={draft.description}
            onChangeText={setDraftField('description')}
            placeholder={t('onboardingWizard.products.descriptionPlaceholder')}
            multiline
          />
          <Input
            testID="business_context_products_priceRange"
            maxLength={MAX_PRODUCT_PRICE_RANGE_LENGTH}
            label={t('onboardingWizard.products.priceRangeLabel')}
            value={draft.priceRange}
            onChangeText={setDraftField('priceRange')}
            placeholder={t('onboardingWizard.products.priceRangePlaceholder')}
          />
          <ProductKindSelector
            testIDPrefix="business_context_products_kind"
            value={draft.kind}
            onChange={(kind) => setDraft((prev) => ({ ...prev, kind }))}
          />
          <ProductAliasesInput
            testIDPrefix="business_context_products_aliases"
            aliases={draft.aliases}
            productName={draft.name}
            onChange={(aliases) => setDraft((prev) => ({ ...prev, aliases }))}
          />
          <Box style={styles.addRow}>
            <Button
              testID="business_context_products_add"
              variant="outline"
              onPress={handleCreate}
              loading={createMutation.isPending}
              disabled={!canCreate}
              accessibilityState={{ disabled: !canCreate }}
              accessibilityLabel={t('settings.businessContext.products.add')}
            >
              {t('settings.businessContext.products.add')}
            </Button>
          </Box>
        </VStack>
      ) : null}

      <DeleteProductDialog
        organizationId={organizationId}
        product={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
      />

      {feedback.errorKey ? (
        <Text
          testID="business_context_products_error"
          variant="bodySmall"
          color={colors.status.error.main}
          accessibilityRole="alert"
        >
          {t(feedback.errorKey, productErrorMessageParams(feedback.errorKey))}
        </Text>
      ) : null}
    </VStack>
  );
}

const styles = StyleSheet.create({
  heading: {
    fontWeight: '600',
  },
  row: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
  addRow: {
    alignSelf: 'flex-start',
  },
});
